import { FC, useCallback, useEffect, useState } from 'react'
import { Form, json, useActionData, useNavigate, useRevalidator } from 'react-router-dom'
import { useUser } from '@clerk/clerk-react'
import './sms-settings.css'

// COMPONENTS
import { createAction } from '@utils/actions'
import Button from '@components/atoms/button'
import Card from '@components/atoms/card'
import Checkbox from '@/components/atoms/checkbox'
import Toast, { IToast } from '@components/molecules/toast'

// TYPES
import type { RouteObject } from 'react-router-dom'
import { Notification } from 'src/_types/settings'

// UTILITIES
import { isNonEmptyArray } from '@utils/type-checkers'
import { createResponseObj, VirtualCardAPI } from '@apis'
import { createAuthenticatedLoader } from '@utils/loaders'

const formId = "add-pledge"
const Component: FC<{}> = () => {
    const navigate = useNavigate()
    const { user } = useUser()
    const [toast, setT] = useState<IToast>()
    const setToast = useCallback((toast: IToast | undefined) => { setT(toast) }, [setT])

    const [initialSettings, setInitialSettings] = useState<Notification[]>([])
    const [settings, setSettings] = useState<Notification[]>([])
    const [updatedSettings, setUpdatedSettings] = useState<Notification[]>([])

    const actionData = useActionData()
    const { revalidate } = useRevalidator()
    useEffect(() => {
        if (actionData === 'success') {
            setToast({ type: 'success', text: 'Settings updated' })
            setUpdatedSettings([])
            revalidate()
        }
    }, [actionData])

    useEffect(() => {
        if (!!user?.publicMetadata.settings && isNonEmptyArray(user?.publicMetadata.settings.notifications)) {
            setSettings(user.publicMetadata.settings.notifications as Notification[])
            setInitialSettings(user.publicMetadata.settings.notifications as Notification[])
        }
    }, [user])

    useEffect(() => {
        console.log({ settings, updatedSettings })
    }, [settings, updatedSettings])

    function updateSetting(type: 'sms' | 'push', setting: Notification, index: number) {
        const arr = [...settings]
        let newSetting = { ...setting }

        if (type === 'sms') {
            const newVal = !setting.smsEnabled
            newSetting = { ...setting, smsEnabled: newVal, enabled: newVal }
        }
        if (type === 'push') {
            const newVal = !setting.pushEnabled || false
            newSetting = { ...setting, pushEnabled: newVal }
        }

        arr[index] = newSetting
        setSettings(arr)

        setUpdatedSettings((currentUpdatedSettings) => {
            const list = [...currentUpdatedSettings]
            const existingIndex = list.findIndex(s => s.id === setting.id)
            const initial = initialSettings.find(s => s.id === setting.id)

            // If it matches initial state, remove it from updated list
            if (initial && initial.smsEnabled === newSetting.smsEnabled && initial.pushEnabled === newSetting.pushEnabled) {
                if (existingIndex > -1) list.splice(existingIndex, 1)
                return list
            }

            // Otherwise upsert
            if (existingIndex > -1) list[existingIndex] = newSetting
            else list.push(newSetting)

            return list
        })
    }

    function handleBack() {
        navigate(-1)
    }

    return (<>
        <div className='sms-settings'>
            <div className='flex flex-row justify-between'>
                <a onClick={handleBack}>
                    <img src="/icons/arrow-left-small.svg" alt="back" style={{ width: '1.4rem', height: '1.4rem' }} draggable="false" />
                </a>
                <h1>Notifications</h1>
                <div></div>
            </div>
            <Form id={formId} method='PUT' style={{ flex: 1 }}>
                <input onChange={() => { }} value={JSON.stringify(updatedSettings)} name="updatedSettings" hidden />
                {settings.sort((a, b) => a.title.localeCompare(b.title)).map((setting, index) => (
                    <Card className='settings-container' key={`${setting.id}-${index}`}>
                        <div className='flex flex-row items-start justify-between'>
                            <div className='flex flex-col'>
                                <p className='text-body'><strong>{setting.title}</strong></p>
                                <p className='text-body'>{setting.description}</p>
                            </div>
                        </div>
                        <div className="flex flex-row items-start justify-between">
                            <p className='text-body'>Text Message</p>
                            <Checkbox
                                checked={setting.smsEnabled}
                                onChange={() => updateSetting('sms', setting, index)}
                            />
                        </div>
                        <div className="flex flex-row items-start justify-between">
                            <p className='text-body'>Push Notification</p>
                            <Checkbox
                                checked={setting.pushEnabled || false}
                                onChange={() => updateSetting('push', setting, index)}
                            />
                        </div>
                    </Card>
                ))}
            </Form>
            <Button form={formId} type="submit" disabled={updatedSettings.length < 1}>Update</Button>
        </div>
        <Toast toast={toast} />
    </>)
}

const loader = createAuthenticatedLoader(async (_, Clerk) => {
    // we need this for existings users, since the settings aren't added retroactively.
    // we can probably eventually remove this once all users have settings in their metadata 
    const user = Clerk.user
    const userId = user?.id
    if (!!userId && !user?.publicMetadata?.settings?.notifications)
        await VirtualCardAPI.UpdateNotifSettings.fetch(userId, { notifications: [] })

    return null
})

const action = createAction(async ({ request }) => {
    try {
        const formData = await request.formData()
        const updatedSettings = JSON.parse(`${formData.get('updatedSettings') || ''}`)
        const _updatedSettings: Partial<Notification[]> = updatedSettings
            .map((setting: Notification) => ({
                id: setting.id,
                enabled: setting.smsEnabled,
                smsEnabled: setting.smsEnabled,
                pushEnabled: setting.pushEnabled
            }))
        const user = window.Clerk?.user

        console.log("Update SMS Settings Data:", { user, _updatedSettings })
        if (!user || !isNonEmptyArray<Partial<Notification[]>>(_updatedSettings)) throw createResponseObj({
            status: 'failed', type: 'local', name: 'missing-resources',
            message: `Missing required resources. ${JSON.stringify({ user, _updatedSettings })}`
        })

        console.log({ updatedSettings: _updatedSettings })
        await VirtualCardAPI.UpdateNotifSettings.fetch(user.id, { notifications: _updatedSettings })

        return json('success', { status: 200 })
    } catch (error) {
        console.error(error)
        return json(error, { status: 500 })
    }
})


const SMSSettingsRoute: RouteObject = {
    path: "sms-settings",
    Component,
    action,
    loader
}

export { SMSSettingsRoute }
